import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useEffect, useState } from 'react'
import { useSaveCompany } from '@/api/hooks'
import type { Company } from '@/api/types'
import { ErrorBanner } from '@/components/common'

const BLANK: Company = {
  id: '',
  code: '',
  name: '',
  legalName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  phone: '',
  email: '',
  website: '',
  gstin: '',
  currencyCode: 'INR',
  timezone: 'Asia/Kolkata',
  active: true,
}

/** Two digits of state code, a ten character PAN, entity number, Z, check digit. */
const GSTIN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/

/**
 * One business: how it trades, where it is, and what it is registered as.
 *
 * <p>The GSTIN here is the company's principal registration. An outlet trading
 * in another state holds its own and overrides it; for a business in one state
 * this is the whole of it, and it is what heads the documents and files the
 * e-invoices.</p>
 *
 * <p>The mark and the bank account are not here. They belong to the branch,
 * beside its address and its FSSAI licence: a bill for food is headed by the
 * premises it came out of and paid into that premises' account.</p>
 */
export function CompanyDialog({
  open,
  company,
  onClose,
}: {
  open: boolean
  company?: Company | null
  onClose: () => void
}) {
  const save = useSaveCompany()
  const [draft, setDraft] = useState<Company>(BLANK)
  const [firstOutletName, setFirstOutletName] = useState('Main outlet')

  const creating = !company

  useEffect(() => {
    if (!open) return
    setDraft(company ? { ...company } : { ...BLANK })
    setFirstOutletName('Main outlet')
    save.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company])

  const set = (patch: Partial<Company>) => setDraft((current) => ({ ...current, ...patch }))

  const gstin = (draft.gstin ?? '').trim().toUpperCase()
  const gstinBad = gstin.length > 0 && !GSTIN.test(gstin)
  const valid = Boolean(draft.code.trim() && draft.name.trim()) && !gstinBad

  const field = (
    label: string,
    key: keyof Company,
    extra: { required?: boolean; disabled?: boolean; helperText?: string; md?: number } = {},
  ) => (
    <Grid item xs={12} md={extra.md ?? 4}>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={(draft[key] as string) ?? ''}
        disabled={extra.disabled}
        required={extra.required}
        helperText={extra.helperText}
        onChange={(event) => set({ [key]: event.target.value } as Partial<Company>)}
        inputProps={{ 'aria-label': label }}
      />
    </Grid>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{creating ? 'New company' : `Edit ${company?.name}`}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {field('Code', 'code', {
            required: true,
            md: 3,
            // Numbering series and the audit trail name the company by its
            // code, so it cannot move once anything has been filed under it.
            disabled: !creating,
            helperText: creating ? 'Short, e.g. SSC' : 'Fixed once created',
          })}
          {field('Trading name', 'name', { required: true, md: 5 })}
          {field('Legal name', 'legalName', { helperText: 'As registered, for invoices' })}

          {field('Address line 1', 'addressLine1', { md: 6 })}
          {field('Address line 2', 'addressLine2', { md: 6 })}
          {field('City', 'city')}
          {field('State', 'state')}
          {field('Pin code', 'postalCode')}

          {field('Phone', 'phone')}
          {field('Email', 'email')}
          {field('Website', 'website')}

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="GSTIN"
              value={draft.gstin ?? ''}
              error={gstinBad}
              // Checked here as well as on the server, because a GSTIN with a
              // typo is only rejected by the portal at the moment an invoice is
              // filed - long after anybody is looking at this screen.
              helperText={gstinBad ? 'Fifteen characters, e.g. 33AABCS1234H1Z5' : 'Leave blank if unregistered'}
              onChange={(event) => set({ gstin: event.target.value.toUpperCase().trim() })}
              inputProps={{ 'aria-label': 'GSTIN', maxLength: 15 }}
            />
          </Grid>
          {field('Currency', 'currencyCode', { md: 2 })}
          {field('Timezone', 'timezone', { md: 3 })}
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={draft.active ? 'ACTIVE' : 'INACTIVE'}
              onChange={(event) => set({ active: event.target.value === 'ACTIVE' })}
              inputProps={{ 'aria-label': 'Status' }}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
          </Grid>

          {/* A company with no outlet can be signed into and then does nothing:
              nowhere to book, nowhere to cook, and every screen asking for an
              outlet it has not got. */}
          {creating ? (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="First outlet"
                value={firstOutletName}
                onChange={(event) => setFirstOutletName(event.target.value)}
                helperText="Created with the company, along with its services"
                inputProps={{ 'aria-label': 'First outlet' }}
              />
            </Grid>
          ) : null}

          {draft.active ? null : (
            <Grid item xs={12}>
              <Alert severity="warning">
                Inactive. It keeps everything it holds, and stops appearing anywhere it could be
                traded from.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || save.isPending}
          onClick={() =>
            save.mutate(
              {
                id: company?.id,
                body: {
                  code: draft.code.trim(),
                  name: draft.name.trim(),
                  legalName: draft.legalName,
                  addressLine1: draft.addressLine1,
                  addressLine2: draft.addressLine2,
                  city: draft.city,
                  state: draft.state,
                  postalCode: draft.postalCode,
                  phone: draft.phone,
                  email: draft.email,
                  website: draft.website,
                  gstin: gstin || undefined,
                  currencyCode: draft.currencyCode,
                  timezone: draft.timezone,
                  firstOutletName: creating ? firstOutletName : undefined,
                  active: draft.active,
                },
              },
              { onSuccess: onClose },
            )
          }
        >
          {save.isPending ? 'Saving…' : creating ? 'Create company' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
